import { useEffect } from 'react';
import { useDrag } from 'react-dnd';
import type { ConnectDragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import type { DragData } from '../../domain/dnd/types';
import { resolveMovableGroupBlockIds } from '../../domain/selection/block-bulk-ops';
import { resolveMovableGroupRowIds } from '../../domain/selection/row-bulk-ops';
import { getEditorStoreState } from '../../domain/store';
import {
  EDITOR_DND_TYPES,
  type EditorDndType,
} from './item-types';
import { useEmailBuilderViewport } from '../../shell/emailBuilderViewportContext';
import { canDragCanvasItem } from './canvas-drag-eligibility';
import {
  canStartEditorDrag,
  canStartRowGroupDrag,
} from './pointer-guard';

function dndTypeForDrag(dragData: DragData): EditorDndType {
  return dragData.kind.startsWith('palette-')
    ? EDITOR_DND_TYPES.PALETTE
    : EDITOR_DND_TYPES.CANVAS_ITEM;
}

function resolveCanvasDragItem(
  dragData: DragData,
  document: ReturnType<typeof getEditorStoreState>['document'],
  selectedBlockIds: readonly string[],
  selectedRowIds: readonly string[],
): DragData {
  if (dragData.kind === 'item-block') {
    const blockIds = resolveMovableGroupBlockIds(
      document,
      selectedBlockIds,
      dragData.blockId,
    );
    return blockIds.length > 1 ? { ...dragData, blockIds } : dragData;
  }

  if (dragData.kind === 'item-row') {
    const rowIds = resolveMovableGroupRowIds(
      document,
      selectedRowIds,
      dragData.rowId,
    );
    return rowIds.length > 1 ? { ...dragData, rowIds } : dragData;
  }

  return dragData;
}

export function useEditorDrag(
  dragData: DragData,
  options?: {
    id?: string;
    rowGroupDrag?: boolean;
    dragLevel?: 'row' | 'group';
    /**
     * When false, skip collecting isDragging so the drag-source component does
     * not re-render during dragstart (Chrome aborts HTML5 drag if the source
     * mutates in that window). Palette items can keep the default true.
     */
    collectDraggingState?: boolean;
  },
): {
  dragRef: ConnectDragSource;
  dragHandleRef: ConnectDragSource;
  isDragging: boolean;
} {
  const { isMobileLayout } = useEmailBuilderViewport();
  const dndType = dndTypeForDrag(dragData);
  const rowGroupDrag = options?.rowGroupDrag === true;
  const dragLevel = options?.dragLevel ?? 'row';
  const collectDraggingState = options?.collectDraggingState !== false;

  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag<
    DragData,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: dndType,
      item: () => {
        const state = getEditorStoreState();
        return resolveCanvasDragItem(
          dragData,
          state.document,
          state.selectedBlockIds,
          state.selectedRowIds,
        );
      },
      canDrag: () => {
        if (isMobileLayout) {
          return false;
        }
        if (dragData.kind.startsWith('palette-')) {
          return canStartEditorDrag(true);
        }
        const state = getEditorStoreState();
        const inRowMultiSelection =
          dragData.kind === 'item-row' &&
          state.selectedRowIds.length > 1 &&
          state.selectedRowIds.includes(dragData.rowId);
        const allowSelectedContainerContentDrag =
          rowGroupDrag &&
          ((dragData.kind === 'item-row' &&
            state.selection.kind === 'row' &&
            (state.selection.rowId === dragData.rowId || inRowMultiSelection)) ||
            (dragData.kind === 'item-group' &&
              state.selection.kind === 'group' &&
              state.selection.groupId === dragData.groupId));
        const eligible = canDragCanvasItem(state, dragData);
        if (!eligible) {
          return false;
        }
        const ownEntityId =
          dragData.kind === 'item-row'
            ? dragData.rowId
            : dragData.kind === 'item-group'
              ? dragData.groupId
              : undefined;
        return rowGroupDrag
          ? canStartRowGroupDrag(
              true,
              dragLevel,
              allowSelectedContainerContentDrag,
              ownEntityId,
            )
          : canStartEditorDrag(true);
      },
      collect: monitor => ({
        isDragging: collectDraggingState ? monitor.isDragging() : false,
      }),
    }),
    [
      collectDraggingState,
      dragData,
      dndType,
      dragLevel,
      isMobileLayout,
      rowGroupDrag,
    ],
  );

  useEffect(() => {
    dragPreviewRef(getEmptyImage(), {
      captureDraggingState: false,
      anchorX: 0,
      anchorY: 0,
    });
  }, [dragPreviewRef]);

  return {
    dragRef,
    dragHandleRef: dragRef,
    isDragging,
  };
}
